import React, { useState, useMemo, useEffect } from 'react';
import { api } from '../../src/services/api-new';
import { useAuth } from '../../src/contexts/AuthContext';
import { addBookingToStorage, BookingAppointment } from '../../services/bookingStorage';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

interface BookingTicket {
    id: string;
    service: any;
    vet: any;
    date: string;
    time: string;
    owner: string;
    pet: string;
}

interface BookingProps {
    services?: any[];
    vets?: any[];
    onComplete?: () => void;
    onBack?: () => void;
}

interface Patient {
    id: number;
    name: string;
    species: string;
    breed?: string;
}

const BookingFlow: React.FC<BookingProps> = ({ services: externalServices, vets: externalVets, onComplete, onBack }) => {
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    const [services, setServices] = useState<any[]>([]);
    const [expertVets, setExpertVets] = useState<any[]>([]);
    const [selectedService, setSelectedService] = useState<any>(null);
    const [selectedVet, setSelectedVet] = useState<any>(null);
    const [selectedDate, setSelectedDate] = useState(0);
    const [selectedTime, setSelectedTime] = useState('09:00 AM');

    const [ownerName, setOwnerName] = useState('');

    // Patient Management
    const [patients, setPatients] = useState<Patient[]>([]);
    const [selectedPatientId, setSelectedPatientId] = useState<number | 'new'>('new');
    const [newPatientName, setNewPatientName] = useState('');
    const [newPatientSpecies, setNewPatientSpecies] = useState('Dog');
    const [newPatientBreed, setNewPatientBreed] = useState('');

    const [ticket, setTicket] = useState<BookingTicket | null>(null);
    const [loading, setLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(true);
    const ticketRef = React.useRef<HTMLDivElement>(null);

    const handleDownloadPDF = async () => {
        if (!ticketRef.current) return;

        try {
            const canvas = await html2canvas(ticketRef.current, {
                scale: 2,
                backgroundColor: '#ffffff',
                logging: false,
                useCORS: true
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const imgWidth = 210;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            pdf.save(`booking-ticket-${ticket?.id || 'new'}.pdf`);
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Failed to generate PDF. Please try again.");
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            // Validate external props are arrays
            const validExternalServices = Array.isArray(externalServices) ? externalServices : [];
            const validExternalVets = Array.isArray(externalVets) ? externalVets : [];

            if (validExternalServices.length > 0) {
                setServices(validExternalServices);
                setSelectedService(validExternalServices[0]);
            }

            if (validExternalVets.length > 0) {
                setExpertVets(validExternalVets);
            }

            // Fetch user's patients if logged in
            if (user) {
                try {
                    const patientsData = await api.patients.getAll();
                    const validPatients = Array.isArray(patientsData) ? patientsData : [];
                    setPatients(validPatients);
                    if (validPatients.length > 0) {
                        setSelectedPatientId(validPatients[0].id);
                    }
                    if (!ownerName) setOwnerName(user.name);
                } catch (error) {
                    console.error("Failed to fetch patients:", error);
                }
            }

            if (validExternalServices.length > 0 && validExternalVets.length > 0) {
                setIsDataLoading(false);
                return;
            }

            setIsDataLoading(true);
            try {
                const [servicesData, vetsData] = await Promise.all([
                    api.services.getAll(),
                    api.vets.getAll()
                ]);

                // Ensure servicesData is an array
                const servicesArray = Array.isArray(servicesData) ? servicesData : [];
                if (!externalServices || externalServices.length === 0) {
                    setServices(servicesArray);
                    if (servicesArray.length > 0) setSelectedService(servicesArray[0]);
                }

                // Ensure vetsData is an array
                const vetsArray = Array.isArray(vetsData) ? vetsData : [];
                if (!externalVets || externalVets.length === 0) {
                    setExpertVets(vetsArray);
                }
            } catch (error) {
                console.error("Failed to fetch booking data:", error);
            } finally {
                setIsDataLoading(false);
            }
        };
        fetchData();
    }, [user, externalServices, externalVets]);

    const dates = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() + i);
            return {
                day: d.toLocaleDateString('en-US', { weekday: 'short' }),
                date: d.getDate(),
                fullString: d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }),
                isoDate: d.toISOString().split('T')[0]
            };
        });
    }, []);

    const convertTo24Hour = (timeStr: string) => {
        const [time, modifier] = timeStr.split(' ');
        let [hours, minutes] = time.split(':');
        if (hours === '12') hours = '00';
        if (modifier === 'PM') hours = String(parseInt(hours, 10) + 12);
        if (hours.length === 1) hours = `0${hours}`;
        return `${hours}:${minutes}`;
    };

    const handleFinalBooking = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            let patientId = selectedPatientId;
            let finalPetName = '';

            // 1. Create Patient if New
            if (patientId === 'new') {
                const newPatientData = {
                    name: newPatientName,
                    species: newPatientSpecies,
                    breed: newPatientBreed,
                    age: 'Unknown',
                    weight: 'Unknown',
                    notes: 'Created during booking'
                };
                const response = await api.patients.create(newPatientData) as any;
                patientId = response.patient.id;
                finalPetName = response.patient.name;

                // Refresh patients list
                const updatedPatients = await api.patients.getAll();
                setPatients(Array.isArray(updatedPatients) ? updatedPatients : []);
            } else {
                const p = patients.find(p => p.id === patientId);
                finalPetName = p ? p.name : 'Unknown';
            }

            // 2. Create Appointment
            const appointmentData = {
                patient_id: patientId,
                service_id: selectedService.id,
                vet_id: selectedVet?.id || expertVets[0]?.id,
                appointment_date: `${dates[selectedDate].isoDate} ${convertTo24Hour(selectedTime)}`,
                notes: `Booked online. Owner: ${ownerName}`
            };

            const response = await api.appointments.create(appointmentData) as any;
            const bookingId = `BK-${response.appointment?.id || Math.floor(1000 + Math.random() * 9000)}`;

            // 3. Update Local Storage for Ticket/Fallback
            const newTicket: BookingTicket = {
                id: bookingId,
                service: selectedService,
                vet: selectedVet || expertVets[0] || { name: 'Doctor' },
                date: dates[selectedDate].fullString,
                time: selectedTime,
                owner: ownerName,
                pet: finalPetName,
            };

            const bookingRecord: BookingAppointment = {
                id: bookingId,
                service: selectedService,
                vet: selectedVet || expertVets[0] || { name: 'Doctor' },
                date: dates[selectedDate].fullString,
                time: selectedTime,
                owner: ownerName,
                pet: finalPetName,
                status: 'PENDING',
                createdAt: new Date().toISOString(),
            };

            addBookingToStorage(bookingRecord);
            setTicket(newTicket);
        } catch (error) {
            console.error("Booking failed:", error);
            alert("Booking failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (isDataLoading) {
        return (
            <div className="h-full flex items-center justify-center bg-surface-light dark:bg-background-dark">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
                    <p className="text-[10px] font-black text-primary uppercase tracking-[0.2em]">Synchronizing Clinic Data...</p>
                </div>
            </div>
        );
    }

    if (ticket) {
        return (
            <div className="h-full overflow-y-auto bg-surface-light dark:bg-background-dark p-6">
                <div className="max-w-md mx-auto animate-fade-in-up">
                    <button
                        onClick={() => onComplete && onComplete()}
                        className="mb-6 flex items-center gap-2 text-gray-400 hover:text-primary transition-colors text-sm font-bold"
                    >
                        <span className="material-icons-round">arrow_back</span>
                        Return to Menu
                    </button>

                    <div ref={ticketRef} style={{ backgroundColor: '#ffffff' }} className="bg-white dark:bg-surface-dark rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-800 relative mb-8">
                        <div className="bg-primary p-10 text-white text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-full opacity-5 opacity-20 bg-white/10"></div>
                            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-md">
                                <span className="material-icons-round text-3xl">pets</span>
                            </div>
                            <h3 className="text-xl font-black relative z-10 tracking-widest uppercase">Booking Confirmed</h3>
                            <p className="text-xs font-bold opacity-80 relative z-10 uppercase tracking-tighter">Your Digital Entry Pass</p>
                        </div>
                        <div className="p-10 space-y-8 relative">
                            <div className="text-center">
                                <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.3em] mb-2">Reference ID</p>
                                <p className="text-5xl font-black text-primary-dark dark:text-white tracking-tighter mb-4">{ticket.id}</p>
                                <Barcode value={ticket.id} />
                            </div>

                            <div className="grid grid-cols-2 gap-8 py-8 border-t border-b border-gray-50 dark:border-gray-800">
                                <div><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Clinic Service</p><p className="font-black text-gray-800 dark:text-gray-200">{ticket.service.label || ticket.service.name}</p></div>
                                <div className="text-right"><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Appointment</p><p className="font-black text-gray-800 dark:text-gray-200">{ticket.date}</p></div>
                                <div><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Time Slot</p><p className="font-black text-gray-800 dark:text-gray-200">{ticket.time}</p></div>
                                <div className="text-right"><p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mb-1">Assigned Dr.</p><p className="font-black text-gray-800 dark:text-gray-200">{ticket.vet.name.split(' ').length > 1 ? ticket.vet.name.split(' ')[1] : ticket.vet.name}</p></div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                        <span className="material-icons-round text-lg">person</span>
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase">Pet Owner</p>
                                        <p className="font-bold text-sm">{ticket.owner}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-right">
                                    <div className="hidden sm:block">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase">Patient</p>
                                        <p className="font-bold text-sm">{ticket.pet}</p>
                                    </div>
                                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                        <span className="material-icons-round text-lg">pets</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Punch Holes for Ticket Aesthetic */}
                        <div className="hidden sm:block absolute left-0 top-[260px] -translate-x-1/2 w-10 h-10 bg-surface-light dark:bg-background-dark rounded-full border border-gray-100 dark:border-gray-800"></div>
                        <div className="hidden sm:block absolute right-0 top-[260px] translate-x-1/2 w-10 h-10 bg-surface-light dark:bg-background-dark rounded-full border border-gray-100 dark:border-gray-800"></div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => { setTicket(null); setStep(1); }}
                            className="w-full bg-white dark:bg-surface-dark text-primary-dark dark:text-white font-black py-4 rounded-2xl border-2 border-gray-100 dark:border-gray-800 hover:border-primary transition-all uppercase tracking-widest text-sm"
                        >
                            Book Another Visit
                        </button>
                        <button
                            onClick={handleDownloadPDF}
                            className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-black py-4 rounded-2xl shadow-xl transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-2 mb-2"
                        >
                            <span className="material-icons-round">download</span>
                            Download Ticket PDF
                        </button>
                        <button
                            onClick={() => onComplete && onComplete()}
                            className="w-full bg-primary hover:bg-primary-dark text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/30 transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-2"
                        >
                            <span className="material-icons-round">check_circle</span>
                            Finish & Return
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto bg-surface-light dark:bg-background-dark p-6 lg:p-12 scroll-smooth">
            <div className="max-w-4xl mx-auto space-y-10">

                {/* Progress Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        {onBack && (
                            <button
                                onClick={onBack}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-surface-dark border border-gray-100 dark:border-gray-800 hover:border-primary transition-all shadow-sm"
                            >
                                <span className="material-icons-round text-gray-600 dark:text-gray-300">arrow_back</span>
                            </button>
                        )}
                        <h1 className="text-2xl font-black text-primary-dark dark:text-white">Book Appointment</h1>
                    </div>
                    <div className="flex items-center">
                        <div className="md:hidden flex gap-2">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className={`w-2.5 h-2.5 rounded-full transition-all ${step >= i ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-800'}`} />
                            ))}
                        </div>
                        <div className="hidden md:flex items-center">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className={`flex items-center ${i < 4 ? 'flex-1' : ''}`}>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm transition-all shadow-lg ${step >= i ? 'bg-primary text-white scale-110 shadow-primary/20' : 'bg-white dark:bg-surface-dark text-gray-300 border border-gray-100 dark:border-gray-800'}`}>
                                        {step > i ? <span className="material-icons-round text-lg">done</span> : i}
                                    </div>
                                    {i < 4 && <div className={`flex-1 h-1 mx-4 rounded-full transition-all w-12 ${step > i ? 'bg-primary' : 'bg-gray-100 dark:bg-gray-800'}`}></div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-surface-dark rounded-[3rem] shadow-[0_32px_64px_-16px_rgba(0,0,0,0.08)] dark:shadow-none border border-gray-100 dark:border-gray-800 overflow-hidden animate-fade-in-up">

                    {/* STEP 1: SERVICE */}
                    {step === 1 && (
                        <div className="p-10 space-y-8">
                            <div className="text-center sm:text-left">
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Select a Service</h2>
                                <p className="text-gray-500 font-medium">What can we help you with today?</p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-6">
                                {services.map(s => {
                                    const isSelected = selectedService?.id === s.id;
                                    const label = s.label || s.name || 'Service';
                                    const desc = s.desc || s.description || 'No description available';

                                    let icon = s.icon;
                                    if (!icon) {
                                        const lowerName = label.toLowerCase();
                                        if (lowerName.includes('dental')) icon = 'clean_hands';
                                        else if (lowerName.includes('checkup') || lowerName.includes('general')) icon = 'medical_services';
                                        else if (lowerName.includes('lab')) icon = 'biotech';
                                        else if (lowerName.includes('surgery')) icon = 'local_hospital';
                                        else if (lowerName.includes('vaccin')) icon = 'vaccines';
                                        else if (lowerName.includes('grooming')) icon = 'content_cut';
                                        else icon = 'pets';
                                    }

                                    return (
                                        <div
                                            key={s.id}
                                            onClick={() => setSelectedService(s)}
                                            className={`group cursor-pointer rounded-[2rem] p-6 sm:p-8 border-2 transition-all flex flex-col gap-4 relative overflow-hidden ${isSelected ? 'border-primary bg-primary/5' : 'border-gray-50 dark:border-gray-800 hover:border-primary/30'}`}
                                        >
                                            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${isSelected ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 group-hover:text-primary'}`}>
                                                <span className="material-icons-round text-3xl">{icon}</span>
                                            </div>
                                            <div>
                                                <h3 className={`font-black text-xl mb-1 ${isSelected ? 'text-primary' : 'text-gray-800 dark:text-white'}`}>{label}</h3>
                                                <p className="text-sm text-gray-500 font-medium leading-relaxed">{desc}</p>
                                            </div>
                                            <div className={`absolute top-8 right-8 font-black text-sm px-3 py-1 rounded-full ${isSelected ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`}>
                                                {s.price}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button onClick={() => setStep(2)} className="w-full bg-primary hover:bg-primary-dark text-white font-black py-5 rounded-2xl shadow-xl shadow-primary/20 transition-all flex items-center justify-center gap-3">
                                Continue to Specialists <span className="material-icons-round">arrow_forward</span>
                            </button>
                        </div>
                    )}

                    {/* STEP 2: DOCTOR */}
                    {step === 2 && (
                        <div className="p-10 space-y-8">
                            <div className="flex items-center justify-between">
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Choose Your Vet</h2>
                                <button onClick={() => setStep(1)} className="text-sm font-bold text-gray-400">Back</button>
                            </div>
                            <div className="space-y-4">
                                {expertVets.map(vet => {
                                    const isSelected = selectedVet?.id === vet.id;
                                    return (
                                        <div
                                            key={vet.id}
                                            onClick={() => setSelectedVet(vet)}
                                            className={`cursor-pointer rounded-3xl p-6 border-2 transition-all flex items-center gap-6 ${isSelected ? 'border-primary bg-primary/5' : 'border-gray-50 dark:border-gray-800'}`}
                                        >
                                            <div className="relative">
                                                <img src={vet.image || 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&h=200&fit=crop'} className="w-16 h-16 rounded-2xl object-cover shadow-lg" alt={vet.name} />
                                                {isSelected && <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center"><span className="material-icons-round text-sm">done</span></div>}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-black text-lg text-primary-dark dark:text-white">{vet.name}</h3>
                                                <p className="text-primary font-bold text-xs uppercase tracking-widest">{vet.specialty || vet.specialization}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <button onClick={() => setStep(3)} disabled={!selectedVet} className="w-full bg-primary hover:bg-primary-dark disabled:bg-gray-100 text-white font-black py-5 rounded-2xl">Pick a Time Slot</button>
                        </div>
                    )}

                    {/* STEP 3: SCHEDULE */}
                    {step === 3 && (
                        <div className="p-10 space-y-8">
                            <div className="flex items-center justify-between">
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Pick a Slot</h2>
                                <button onClick={() => setStep(2)} className="text-sm font-bold text-gray-400">Back</button>
                            </div>
                            <div className="space-y-6">
                                <div className="flex gap-4 overflow-x-auto pb-4">
                                    {dates.map((d, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setSelectedDate(i)}
                                            className={`shrink-0 w-20 h-24 rounded-3xl flex flex-col items-center justify-center gap-1 transition-all ${selectedDate === i ? 'bg-primary text-white' : 'bg-gray-50 dark:bg-gray-800 text-gray-500'}`}
                                        >
                                            <span className="text-[10px] uppercase font-black">{d.day}</span>
                                            <span className="text-2xl font-black">{d.date}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {['09:00 AM', '10:30 AM', '01:00 PM', '02:30 PM', '04:00 PM', '05:30 PM'].map(time => (
                                        <button
                                            key={time}
                                            onClick={() => setSelectedTime(time)}
                                            className={`py-4 rounded-2xl font-black text-sm border-2 transition-all ${selectedTime === time ? 'border-primary bg-primary/5 text-primary' : 'border-gray-100 text-gray-400'}`}
                                        >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <button onClick={() => setStep(4)} className="w-full bg-primary text-white font-black py-5 rounded-2xl">Your Details</button>
                        </div>
                    )}

                    {/* STEP 4: FORM */}
                    {step === 4 && (
                        <form onSubmit={handleFinalBooking} className="p-10 space-y-10">
                            <div className="flex items-center justify-between">
                                <h2 className="text-3xl font-black text-primary-dark dark:text-white tracking-tight">Your Details</h2>
                                <button onClick={() => setStep(3)} className="text-sm font-bold text-gray-400">Back</button>
                            </div>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <input required value={ownerName} onChange={e => setOwnerName(e.target.value)} className="w-full px-6 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 font-bold outline-none border-2 border-transparent focus:border-primary" placeholder="Your Name" />

                                    <div className="space-y-3">
                                        <label className="text-xs font-black text-gray-400 uppercase">Select Patient</label>
                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                            {patients.map(p => (
                                                <button key={p.id} type="button" onClick={() => setSelectedPatientId(p.id)} className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${selectedPatientId === p.id ? 'border-primary bg-primary/10 text-primary' : 'border-gray-100'}`}>{p.name}</button>
                                            ))}
                                            <button type="button" onClick={() => setSelectedPatientId('new')} className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${selectedPatientId === 'new' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-100'}`}>+ New</button>
                                        </div>
                                    </div>

                                    {(selectedPatientId === 'new' || patients.length === 0) && (
                                        <div className="space-y-4">
                                            <input required value={newPatientName} onChange={e => setNewPatientName(e.target.value)} className="w-full px-6 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 font-bold outline-none border-2 border-transparent focus:border-primary" placeholder="Pet Name" />
                                            <div className="grid grid-cols-2 gap-4">
                                                <select value={newPatientSpecies} onChange={e => setNewPatientSpecies(e.target.value)} className="w-full px-4 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 font-bold outline-none">
                                                    <option value="Dog">Dog</option>
                                                    <option value="Cat">Cat</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                                <input value={newPatientBreed} onChange={e => setNewPatientBreed(e.target.value)} className="w-full px-4 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800 font-bold outline-none" placeholder="Breed" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="bg-gray-50 dark:bg-gray-800/50 p-8 rounded-[2rem] space-y-4 border border-gray-100">
                                    <h4 className="font-black uppercase text-xs text-gray-400">Summary</h4>
                                    <SummaryItem icon="medical_services" label="Service" value={selectedService?.label || selectedService?.name} />
                                    <SummaryItem icon="person" label="Doctor" value={selectedVet?.name} />
                                    <SummaryItem icon="event" label="Date" value={dates[selectedDate].fullString} />
                                    <SummaryItem icon="schedule" label="Time" value={selectedTime} />
                                    <div className="pt-4 border-t border-gray-200 flex justify-between items-center">
                                        <span className="font-black text-xs text-gray-400 uppercase">Total</span>
                                        <span className="text-xl font-black text-primary">{selectedService?.price}</span>
                                    </div>
                                </div>
                            </div>
                            <button type="submit" disabled={loading} className="w-full bg-primary text-white font-black py-6 rounded-3xl shadow-xl hover:-translate-y-0.5 transition-all">
                                {loading ? 'Processing...' : 'Confirm Appointment'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

const SummaryItem = ({ icon, label, value }: any) => (
    <div className="flex justify-between items-center">
        <div className="flex items-center gap-2 text-gray-400">
            <span className="material-icons-round text-lg">{icon}</span>
            <span className="text-[10px] font-black uppercase">{label}</span>
        </div>
        <span className="font-black text-xs">{value}</span>
    </div>
)

const Barcode = ({ value }: { value: string }) => (
    <div className="flex justify-center items-end h-12 gap-0.5 opacity-80">
        {value.split('').map((_, i) => (
            <div key={i} className="bg-gray-800 dark:bg-white flex-1" style={{ height: `${Math.random() * 50 + 50}%`, minWidth: '2px' }} />
        ))}
    </div>
);

export default BookingFlow;
